<template>
  <div>
    <vs-tabs v-model="tabIndex">
      <vs-tab label="전체"></vs-tab>
      <vs-tab label="직영"></vs-tab>
      <vs-tab label="수급업체"></vs-tab>
    </vs-tabs>

    <!-- Table -->
    <list-page-layout
      ref="listPageLayout"
      approval
      useCardBody
      useSelectPerPage
      :selectPerPage="{
        default: 10,
        options: [10, 20, 30, 50],
      }"
      usePagination
      :pagination="{
        default: 1,
        lastPage,
        maxItems: total,
      }"
      @search="firstLoad = true"
      @load="loadList"
    >
      <template v-slot:titles>
        <div class="mr-auto pl-2 xl:pl-0 flex items-center mb-4" style="font-size: 18px">
          <span class="mr-2" style="font-weight: 700">위험성평가 실시건수 : </span>
          <span>{{ total }}건</span>
        </div>
      </template>

      <template v-slot:buttons>
        <vs-button class="mb-4 mr-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickExcelDownload">List Excel 다운로드</vs-button>
        <!-- 위험성평가 상세 Raw data 엑셀 (src/util/RiskRawExcel.js 참고) -->
        <vs-button v-if="riskRawExcelVisible" class="mb-4 mr-4 flex-1 xl:flex-none" color="secondary" type="border" @click="onClickRiskRawExcel">Raw Excel 다운로드</vs-button>
        <vs-button class="mb-4 py-0 pl-6 hidden xl:block" color="secondary" type="border" @click="multiPrintViewer = true">
          <div class="flex items-center">
            <feather-icon icon="PrinterIcon" svgClasses="h-6 w-6" class="mr-1" />
            <span class="my-3">선택 후 인쇄</span>
          </div>
        </vs-button>
      </template>

      <template v-slot:search>
        <!-- 검색어 입력창에서 Enter 를 누르면 [검색] 버튼과 동일하게 1페이지부터 다시 조회한다. -->
        <PageSearch v-model="nav" :options="options" @search="$refs.listPageLayout.onClickSearch()" />
      </template>

      <theme-simple-table approval :boardId="Number(`${boardId}`)" :list="list" @selected="onClickSelect" @sort="onClickSort" />
    </list-page-layout>

    <popup-excel-download v-model="openExcelDownload" :list="excelDownloadList" title="위험성평가" />
    <popup-multi-print-viewer
      v-model="multiPrintViewer"
      :boardId="814"
      :isApproval="true"
      :searchComponent="searchComponent"
      :searchOption="options"
      :nav="nav"
      :method="{
        getContent: this.getContent,
        getCreatedDate: this.getCreatedDate,
        getCompanyName: this.getCompanyName,
        getContentTargetList: this.getContentTargetList,
        onNameBlur: this.onNameBlur,
        getContentRiskList: this.getContentRiskList,
        getContentStringList: this.getContentStringList,
        getAuthUserInfo: this.getAuthUserInfo,
        comma: this.comma,
      }"
    ></popup-multi-print-viewer>
  </div>
</template>

<script>
import ListPageLayout from "@/views/list/ListPageLayout.vue";
import ThemeSimpleTable from "@/views/list/TableListPageTheme/ThemeSimpleTable.vue";
import PopupExcelDownload from "@/popup/PopupExcelDownload.vue";
import EmptySpace from "@/views/list/components/EmptySpace.vue";
import HttpRequest from "@/util/HttpRequest";
import PopupMultiPrintViewer from "@/popup/PopupMultiPrintViewer.vue";
import PageSearch from "./PageSearch.vue";
import { CLASSIFICATION_BOARD_MAP, buildLevelOptions, loadClassificationTables, teamBodyKey, toClassificationBoardIds } from "@/util/ClassificationTable";
// 위험성평가 상세 Raw data 엑셀
import { canDownloadRiskRawExcel, downloadRiskRawExcel } from "@/util/RiskRawExcel";

// 팀(업체)명/공장명/대분류/중분류/소분류는 813/814에 저장된 raw 스냅샷이 아니라,
// 736/895의 "현재" 데이터를 기준으로 한다.
const CLASSIFICATION_TEAM_FIELD = { 736: "body.팀명", 895: "body.업체명" };

// 검색조건(키워드) head → 공정분류표(736/895) body 키. 팀(업체)명은 게시판별로 달라 null 로 두고 CLASSIFICATION_TEAM_FIELD 를 쓴다.
const CLASSIFICATION_KEYWORD_FIELD = {
  "팀(업체)명": null,
  "공장명": "body.공장명",
  "대분류": "body.대분류",
  "중분류": "body.중분류",
  "소분류": "body.소분류",
};

// latestClassification(736/895 최신 body)에서 팀(업체)명을 꺼낸다. 736은 팀명, 895는 업체명.
function latestTeamName(item) {
  const latest = item && item.latestClassification;
  if (!latest) return "";
  const classificationBoardId = CLASSIFICATION_BOARD_MAP[item.board_id];
  return latest[teamBodyKey(classificationBoardId)] || "";
}

// 목록/엑셀에 표시할 분류값: 736/895 최신값을 우선하고, 없으면 문서에 저장된 raw 값으로 폴백한다.
function latestOrRaw(item, content, key) {
  const latest = item && item.latestClassification;
  if (latest && latest[key]) return latest[key];
  const raw = content && content.body && content.body.공정분류 && content.body.공정분류.raw;
  return (raw && raw[key]) || "";
}

export default {
  name: "Page",
  components: {
    EmptySpace,
    PopupExcelDownload,
    ThemeSimpleTable,
    ListPageLayout,
    PopupMultiPrintViewer,
    PageSearch,
  },
  watch: {
    tabIndex() {
      this.firstLoad = true;
      this.$refs.listPageLayout.pagination.default = 1;
      this.$refs.listPageLayout.loadList();
      this.excelList = [];
      // 검색 조건 초기화
      this.nav = {
        year: "",
        fieldName: "",
        teamName: "",
        date: { sd: "", ed: "" },
        importantRisk: [],
        approvalDate: { sd: "", ed: "" },
        field: { fieldId: "", fieldName: "" },
        column: { head: "", keyword: "" },
        사업장: "전체",
        팀업체명: "전체",
        공장명: "전체",
        대분류: "전체",
        중분류: "전체",
        소분류: "전체",
        구분2: "전체",
        구분3: "전체",
      };
    },
    page() {
      this.$refs.listPageLayout.pagination.default = this.page;
      this.$refs.listPageLayout.loadList();
    },
    // 검색 조건이 바뀔 때마다 즉시 조회하면 DB 부하가 커지므로,
    // 목록 조회(reloadList)는 [검색] 버튼을 눌렀을 때에만 수행한다.
    // 계층형 드롭다운은 하위 선택지 갱신을 위해 loadOptions / 하위 선택 초기화만 즉시 수행한다.
    "nav.사업장"() {
      this.nav.팀업체명 = "전체";
      this.nav.공장명 = "전체";
      this.nav.대분류 = "전체";
      this.nav.중분류 = "전체";
      this.nav.소분류 = "전체";
      this.loadOptions();
    },
    "nav.팀업체명"() {
      this.nav.공장명 = "전체";
      this.nav.대분류 = "전체";
      this.nav.중분류 = "전체";
      this.nav.소분류 = "전체";
      this.loadOptions();
    },
    "nav.공장명"() {
      this.nav.대분류 = "전체";
      this.nav.중분류 = "전체";
      this.nav.소분류 = "전체";
      this.loadOptions();
    },
    "nav.대분류"() {
      this.nav.중분류 = "전체";
      this.nav.소분류 = "전체";
      this.loadOptions();
    },
    "nav.중분류"() {
      this.nav.소분류 = "전체";
      this.loadOptions();
    },
    "nav.소분류"() {
      this.loadOptions();
    },
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return this.$route.params.boardId;
    },
    boardIds() {
      switch (this.tabIndex) {
        case 0:
          return [813, 814];
        case 1:
          return [813];
        case 2:
          return [814];
      }
    },

    permission() {
      const boards = this.$store.state.member.memberInfo.boards;
      if (boards) {
        let board = boards.find((b) => parseInt(b.id) === parseInt(this.boardId));
        if (!!board && !!board.pivot) {
          if (board.pivot.project_id) {
            // 프로젝트에 맞는 권한이 있는지 다시 조사한다
            board = boards.find((b) => parseInt(b.pivot.board_id) === parseInt(this.boardId) && parseInt(b.pivot.project_id) === parseInt(this.projectId));
            return { ...board.pivot };
          } else {
            return { ...board.pivot };
          }
        }
      }
      return {
        c: 0,
        r: 0,
        u: 0,
        d: 0,
        v: 0,
      };
    },

    // 현재 페이지에 표시할 목록들
    currentPageItems() {
      const list = JSON.parse(JSON.stringify(this.list));

      // 정렬
      this.setListSort(list);

      // 페이징 처리
      const start = (this.page - 1) * this.perPage;
      const end = start + this.perPage;
      return list.slice(start, end);
    },

    // 신규작성 사용가능 여부
    createEnabled() {
      if (this.permission.c === 0) return false;
      return true;
    },

    excelDownloadList() {
      const list = JSON.parse(JSON.stringify(this.excelList));

      // 정렬
      this.setListSort(list);

      const excelList = [];
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        const content = JSON.parse(item.post_content);
        excelList.push({
          "No.": list.length - i,
          구분1: item.board_id === 813 ? "직영" : "수급업체",
          구분2: content.body.공정분류.구분,
          구분3: content.body.위험성평가구분,
          사업장명: content.body.stdInfo.field_name,
          "팀(업체)명": latestTeamName(item) || content.body.위험성평가정보.팀 || "",
          공장명: latestOrRaw(item, content, "공장명"),
          대분류: latestOrRaw(item, content, "대분류"),
          중분류: latestOrRaw(item, content, "중분류"),
          소분류: latestOrRaw(item, content, "소분류"),
          작성자: content.body.stdInfo.writer_name,
          중요위험: content.body.위험성평가표.중요위험,
          평가일: content.body.위험성평가정보.평가일자,
          공동작성유무: content.body.공동작성 ? (content.body.공동작성.공동작성여부 === "N" ? "N - DKS 위험성평가 신규작성 필요" : "Y") : "",
          아차사고: content.body.공정분류.아차사고,
        });
      }
      return excelList;
    },

    searchComponent() {
      return PageSearch;
    },

    // Raw data 엑셀 버튼 노출 여부 (시스템관리자 또는 클라이언트 내부 직원)
    riskRawExcelVisible() {
      return canDownloadRiskRawExcel(this.$store);
    },
  },
  data() {
    return {
      nav: {
        year: "",
        fieldName: "",
        teamName: "",
        date: { sd: "", ed: "" },
        importantRisk: [],
        approvalDate: { sd: "", ed: "" },
        field: { fieldId: "", fieldName: "" },
        column: { head: "", keyword: "" },
        사업장: "전체",
        팀업체명: "전체",
        공장명: "전체",
        대분류: "전체",
        중분류: "전체",
        소분류: "전체",
        구분2: "전체",
        구분3: "전체",
      },
      options: {
        사업장: [],
        팀명: [],
        공장명: [],
        대분류: [],
        중분류: [],
        소분류: [],
      },
      // 옵션 로딩을 위한 캐시 관련 속성
      optionsCache: {},
      pendingRequests: {},
      // 탭이 변경됐을 때, 페이지를 초기화하기 위한 플래그
      lastTabIndex: -1,
      // 마지막에 검색됐던 조건을 가져온다
      lastCondition: [],
      tabIndex: 0,
      firstLoad: true,
      list: [],
      excelList: [],
      page: 1,
      perPage: 10,
      lastPage: 1,
      total: 0,
      defaultFunc: undefined,
      sortDefaultKey: "body.위험성평가정보.평가일자",
      sort: {
        index: 0,
        key: "",
        order: "",
      },
      openExcelDownload: false,

      // 선택인쇄
      multiPrintViewer: false,
    };
  },
  created() {
    this.sort = {
      index: 0,
      key: this.sortDefaultKey,
      order: "desc",
    };
  },

  methods: {
    // 사업장 옵션: 실제 평가 문서(813/814)가 있는 사업장만 보여줘야 하므로 813/814에서 조회한다.
    // 팀(업체)명 이하는 공정분류표(736/895) 기준이므로 loadProcessOptions 에서 따로 처리한다.
    loadOptions() {
      // 아래 사업장 조회는 캐시에 걸리면 중간에 return 하므로, 계단식 옵션 갱신은 반드시 먼저 호출해야 한다.
      // (팀(업체)명을 바꿔도 사업장 조회의 cacheKey 는 그대로라 캐시에 걸린다)
      this.loadProcessOptions();

      const keywords = [this.nav.사업장 && this.nav.사업장 !== "전체" ? this.nav.사업장 : ""];

      const filters = [];
      if (this.nav.사업장 && this.nav.사업장 !== "전체") {
        filters.push({ key: "field.field_name", operator: "=", value: this.nav.사업장 });
      }

      const cacheKey = JSON.stringify({ boardIds: this.boardIds, keywords, filters });

      if (this.pendingRequests[cacheKey]) {
        return this.pendingRequests[cacheKey];
      }

      const cachedData = this.optionsCache[cacheKey];
      if (cachedData && new Date().getTime() - cachedData.timestamp < 5 * 60 * 1000) {
        return Promise.resolve(cachedData.data);
      }

      const request = HttpRequest.get("/api/v1/post/options", {
        params: {
          boardIds: this.boardIds,
          options: ["field.field_name"],
          keywords,
          filters,
          onlyApproved: true,
        },
      })
        .then((response) => {
          if (response) {
            if (keywords[0] === "" && response["field.field_name"]) {
              this.options.사업장 = ["전체", ...response["field.field_name"].sort((a, b) => (a > b ? 1 : a < b ? -1 : 0))];
            }

            this.optionsCache[cacheKey] = { data: response, timestamp: new Date().getTime() };
            delete this.pendingRequests[cacheKey];
            return response;
          }
        })
        .catch((err) => {
          console.log("err", err);
          delete this.pendingRequests[cacheKey];
          throw err;
        });

      this.pendingRequests[cacheKey] = request;

      return request;
    },

    // 팀(업체)명/공장명/대분류/중분류/소분류 옵션: 공정분류표(736/895) 전체를 한 번만 받아 두고
    // (ClassificationTable 유틸이 캐시), 사업장/상위 레벨 선택에 따른 단계별 필터링은 모두 프론트에서 계산한다.
    async loadProcessOptions() {
      try {
        const rows = await loadClassificationTables(this.$store, toClassificationBoardIds(this.boardIds));
        const context = {
          fieldName: this.nav.사업장,
          parentValues: [this.nav.팀업체명, this.nav.공장명, this.nav.대분류, this.nav.중분류],
        };

        this.options.팀명 = buildLevelOptions(rows, "팀업체명", context);
        this.options.공장명 = buildLevelOptions(rows, "공장명", context);
        this.options.대분류 = buildLevelOptions(rows, "대분류", context);
        this.options.중분류 = buildLevelOptions(rows, "중분류", context);
        this.options.소분류 = buildLevelOptions(rows, "소분류", context);
      } catch (err) {
        console.log("loadProcessOptions err", err);
      }
    },

    // 검색 실행용: 현재 boardIds에 대응하는 736/895 각각에서
    // 선택된 팀(업체)명/공장명/대분류/중분류/소분류 조건에 맞는 raw.id를 모두 모은다.
    async loadMatchingClassificationIds() {
      const results = await Promise.all(
        toClassificationBoardIds(this.boardIds).map(async (classificationBoardId) => {
          const conditions = this.buildClassificationConditions(classificationBoardId);
          const response = await this.$store.dispatch("board/LOAD_POST_LIST", {
            boardId: classificationBoardId,
            page: 1,
            perPage: 2000,
            conditions,
          });
          return (response && response.data) || [];
        })
      );

      return results.flat().map((post) => Number(post.id));
    },

    buildClassificationConditions(classificationBoardId) {
      const conditions = [];
      if (this.nav.사업장 && this.nav.사업장 !== "전체") {
        conditions.push({ key: "body.사업장.field_name", operator: "=", value: this.nav.사업장 });
      }
      if (this.nav.팀업체명 && this.nav.팀업체명 !== "전체") {
        conditions.push({ key: CLASSIFICATION_TEAM_FIELD[classificationBoardId], operator: "=", value: this.nav.팀업체명 });
      }
      if (this.nav.공장명 && this.nav.공장명 !== "전체") {
        conditions.push({ key: "body.공장명", operator: "=", value: this.nav.공장명 });
      }
      if (this.nav.대분류 && this.nav.대분류 !== "전체") {
        conditions.push({ key: "body.대분류", operator: "=", value: this.nav.대분류 });
      }
      if (this.nav.중분류 && this.nav.중분류 !== "전체") {
        conditions.push({ key: "body.중분류", operator: "=", value: this.nav.중분류 });
      }
      if (this.nav.소분류 && this.nav.소분류 !== "전체") {
        conditions.push({ key: "body.소분류", operator: "=", value: this.nav.소분류 });
      }
      // 검색조건(키워드)에서 분류 항목을 고른 경우: 문서에 저장된 raw 스냅샷이 아니라 736/895 최신값에 LIKE 로 찾는다.
      const keywordSearch = this.classificationKeywordSearch(classificationBoardId);
      if (keywordSearch) {
        conditions.push({ key: keywordSearch.key, operator: "LIKE", value: `%${keywordSearch.keyword}%` });
      }
      return conditions;
    },

    // 검색조건(키워드)의 head 가 팀(업체)명/공장명/대분류/중분류/소분류이면
    // 해당 공정분류표(736/895)의 body 키와 키워드를 돌려준다. 그 외에는 null.
    classificationKeywordSearch(classificationBoardId) {
      const { head, keyword } = this.nav.column || {};
      if (!head || !keyword) return null;
      const bodyKey = CLASSIFICATION_KEYWORD_FIELD[head];
      if (bodyKey === undefined) return null;
      // 팀(업체)명은 736은 팀명, 895는 업체명이라 게시판별로 키가 다르다.
      const key = bodyKey || CLASSIFICATION_TEAM_FIELD[classificationBoardId];
      return { key, keyword };
    },

    // 검색버튼을 누를 경우 / 페이지를 변경 했을 경우 호출됨.
    // 페이지가 열리자마자 page 가 0에서 1로 설정되면서, 한번 호출함. (k-pagination 에서 page 를 0으로 설정해놨기 때문)
    async loadList({ page, perPage, func }) {
      this.loadOptions();

      if (this.lastTabIndex !== this.tabIndex) {
        this.lastTabIndex = this.tabIndex;
        this.page = 1;
      } else {
        this.page = page;
      }

      this.perPage = perPage;

      this.list = [];
      this.loading(true);
      if (this.tabIndex === 0) {
        this.list.push(...(await this.loadBoardList(perPage, func, 813, 814)));
      } else if (this.tabIndex === 1) {
        this.list.push(...(await this.loadBoardList(perPage, func, 813)));
      } else if (this.tabIndex === 2) {
        this.list.push(...(await this.loadBoardList(perPage, func, 814)));
      }

      this.loading(false);
    },

    async getConditions() {
      const conditions = [];

      if (this.nav.year) {
        conditions.push({ key: "updated_at", operator: ">=", value: `${this.nav.year}-01-01` });
        conditions.push({ key: "updated_at", operator: "<=", value: `${this.nav.year}-12-31` });
      }

      // 검색일
      if (this.nav.date) {
        if (this.nav.date.sd) {
          conditions.push({ key: "body.위험성평가정보.평가일자", operator: ">=", value: this.nav.date.sd });
        }
        if (this.nav.date.ed) {
          conditions.push({ key: "body.위험성평가정보.평가일자", operator: "<=", value: this.nav.date.ed });
        }
      }

      // 중요위험
      if (this.nav.importantRisk) {
        conditions.push({ key: "body.위험성평가표.중요위험", operator: "IN", value: this.nav.importantRisk });
      }

      // 사업장
      if (this.nav.사업장 && this.nav.사업장 !== "전체") {
        conditions.push({ key: "field.field_name", operator: "=", value: this.nav.사업장 });
      }

      // 팀(업체)명/공장명/대분류/중분류/소분류: 문서에 저장된 값(raw / 위험성평가정보.팀)으로 매칭하지 않고,
      // 736/895에서 조건에 맞는 id를 찾아 raw.id로 매칭한다.
      // (736/895가 리네임되면 문서에 저장된 이름은 옛 이름이라 텍스트로는 못 찾기 때문)
      // 검색조건(키워드)에서 소분류 등 분류 항목을 고른 경우도 같은 경로로 736/895 LIKE 조회 후 raw.id 매칭한다.
      // 여기서는 "분류 항목 키워드가 있는지"만 판단하므로 게시판 id(736)는 어떤 값이든 결과가 같다.
      // 실제 736/895별 키 결정은 buildClassificationConditions 안에서 게시판별로 다시 호출한다.
      const hasClassificationKeyword = !!this.classificationKeywordSearch(736);
      if (
        hasClassificationKeyword ||
        (this.nav.팀업체명 && this.nav.팀업체명 !== "전체") ||
        (this.nav.공장명 && this.nav.공장명 !== "전체") ||
        (this.nav.대분류 && this.nav.대분류 !== "전체") ||
        (this.nav.중분류 && this.nav.중분류 !== "전체") ||
        (this.nav.소분류 && this.nav.소분류 !== "전체")
      ) {
        const ids = await this.loadMatchingClassificationIds();
        if (ids.length === 0) {
          conditions.push({ key: "body.공정분류.raw.id", operator: "=", value: -1 });
        } else {
          conditions.push({ key: "body.공정분류.raw.id", operator: "IN", value: ids });
        }
      }

      // 구분2
      if (this.nav.구분2 && this.nav.구분2 !== "전체") {
        conditions.push({ key: "body.공정분류.구분", operator: "=", value: this.nav.구분2 });
      }

      // 구분3
      if (this.nav.구분3 && this.nav.구분3 !== "전체") {
        conditions.push({ key: "body.위험성평가구분", operator: "=", value: this.nav.구분3 });
      }

      // 결재 완료일
      if (this.nav.approvalDate && this.nav.approvalDate.startDate) {
        conditions.push({ key: "updated_at", operator: ">=", value: this.nav.approvalDate.startDate });
      }
      if (this.nav.approvalDate && this.nav.approvalDate.endDate) {
        conditions.push({ key: "updated_at", operator: "<=", value: this.nav.approvalDate.endDate });
      }

      // 검색조건
      /*
        column: { head: '', keyword: '' }
       */
      const headKeys = {
        구분1: "boardId",
        구분2: "body.공정분류.구분",
        구분3: "body.위험성평가구분",
        사업장명: "body.stdInfo.field_name",
        "팀(업체)명": "body.위험성평가정보.팀",
        공장명: "body.공정분류.raw.공장명",
        대분류: "body.공정분류.raw.대분류",
        중분류: "body.공정분류.raw.중분류",
        소분류: "body.공정분류.raw.소분류",
        작성자: "body.stdInfo.writer_name",
        중요위험: "body.위험성평가표.중요위험",
        공동작성유무: "body.공동작성.공동작성여부",
        위험도: "body.위험성평가표.위험도",
        아차사고: "body.공정분류.아차사고",
        문서번호: "body.stdInfo.문서번호",
      };

      // 분류 항목(소분류 등)은 위에서 736/895 → raw.id 매칭으로 이미 처리했으므로 raw 스냅샷 LIKE 는 걸지 않는다.
      if (this.nav.column.head && this.nav.column.keyword && !hasClassificationKeyword) {
        conditions.push({ key: headKeys[this.nav.column.head], operator: "LIKE", value: `%${this.nav.column.keyword}%` });
      }

      return conditions;
    },

    async loadBoardList(perPage, func, boardId, withBoardId) {
      const projectId = this.projectId;
      const page = this.page;
      const conditions = await this.getConditions();

      const options = {
        projectId,
        boardSlug: `board-${boardId}`,
        isParent: false,
        page,
        perPage,
        conditions,
        orderTarget: "updated_at",
        orderDirection: "desc",
      };
      if (withBoardId) {
        options.withSlugs = [`board-${withBoardId}`];
      }

      const _data = await this._loadDefragmentation(options);
      const dateFixed = _data.map((item) => {
        try {
          const content = JSON.parse(item.post_content);

          const dateStr = content.body.위험성평가정보.평가일자;
          if (!dateStr) return item;

          const date = new Date(dateStr);
          const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
          content.body.위험성평가정보.평가일자 = formattedDate;
          return {
            ...item,
            post_content: JSON.stringify(content),
          };
        } catch {
          return item;
        }
      });

      return await this.attachLatestClassification(dateFixed);
    },

    // 목록에 표시되는 공장명/대분류/중분류/소분류는 문서 저장 시점의 736/895 스냅샷(raw)이라
    // 736/895가 리네임되면 옛 이름으로 남는다. raw.id로 736/895 최신 데이터를 일괄 조회해서 latestClassification으로 붙여준다.
    async attachLatestClassification(items) {
      const linkByPostId = {}; // item.id -> { classificationBoardId, rawId }
      const idsByClassificationBoard = {};

      for (const item of items) {
        try {
          const content = JSON.parse(item.post_content);
          const rawId = content.body && content.body.공정분류 && content.body.공정분류.raw && content.body.공정분류.raw.id;
          const classificationBoardId = CLASSIFICATION_BOARD_MAP[item.board_id];
          if (rawId && classificationBoardId) {
            linkByPostId[item.id] = { classificationBoardId, rawId };
            if (!idsByClassificationBoard[classificationBoardId]) idsByClassificationBoard[classificationBoardId] = [];
            if (!idsByClassificationBoard[classificationBoardId].includes(rawId)) idsByClassificationBoard[classificationBoardId].push(rawId);
          }
        } catch (e) {
          // 개별 항목 파싱 실패는 무시
        }
      }

      const classificationBoardIds = Object.keys(idsByClassificationBoard);
      if (classificationBoardIds.length === 0) return items;

      const latestByKey = {}; // "classificationBoardId:rawId" -> body
      try {
        await Promise.all(
          classificationBoardIds.map(async (classificationBoardId) => {
            const ids = idsByClassificationBoard[classificationBoardId];

            // 엑셀 다운로드처럼 행이 많을 때 postIds 가 길어져 쿼리스트링 길이 제한에 걸리지 않도록 나눠서 요청한다.
            const CHUNK_SIZE = 100;
            for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
              const chunk = ids.slice(i, i + CHUNK_SIZE);
              const response = await this.$store.dispatch("board/LOAD_POST_LIST", {
                boardId: Number(classificationBoardId),
                page: 1,
                perPage: chunk.length,
                postIds: chunk,
              });
              for (const post of (response && response.data) || []) {
                try {
                  latestByKey[`${classificationBoardId}:${post.id}`] = JSON.parse(post.post_content).body;
                } catch (e) {
                  // 개별 항목 파싱 실패는 무시
                }
              }
            }
          })
        );
      } catch (err) {
        console.log("attachLatestClassification err", err);
        return items; // 실패 시 raw 값 그대로 표시
      }

      return items.map((item) => {
        const link = linkByPostId[item.id];
        if (!link) return item;
        const latest = latestByKey[`${link.classificationBoardId}:${link.rawId}`];
        return { ...item, latestClassification: latest || null };
      });
    },

    // options.page 에 설정된 페이지부터 마지막 페이지까지 데이터를 불러옴
    async _loadDefragmentation(options) {
      const response = await this.$store.dispatch("approval/LOAD_POST_ALL", { ...options, statusIds: [4] /*4:승인*/ });
      this.page = response.current_page;
      this.lastPage = response.last_page;
      this.total = response.total;

      // response.data 각 아이템에 no 값을 설정한다
      response.data.forEach((item, index) => {
        item.no = response.total - ((response.current_page - 1) * options.perPage + index);
      });

      return response.data;
    },

    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },

    // dotted path를 통해서 json 데이터를 찾는다
    // ex> findJsonDataWithDottedPath({a: {b: {c: 1}}}, 'a.b.c') => 1
    findJsonDataWithDottedPath(json, path) {
      try {
        const _keys = Object.keys(json);
        if (_keys.length === 0) {
          return "";
        }
        const paths = path.split(".");
        let result = json;
        for (const p of paths) {
          result = result[p];
        }
        return result;
      } catch (err) {
        return "";
      }
    },

    onClickNew() {
      const boardId = this.boardId;
      const query = this.$route.query;
      const pageType = "write";
      this.$router.push({
        name: "write|tab",
        params: { boardId, pageType },
        query,
      });
    },

    onClickSelect(item) {
      if (!item.postId) return;

      const proj_id = item.project_id;
      const boardId = item.board_id;
      const postId = item.postId;
      const query = this.$route.query;
      const pageType = "write";

      const route = {
        name: "write|update|tab",
        params: {
          proj_id,
          boardId,
          postId,
          pageType,
        },
        query,
      };

      window.open(this.$router.resolve(route).href, "blank");
    },

    onClickSort(sort) {
      if (sort.order === "") {
        this.sort = {
          index: 0,
          key: this.sortDefaultKey,
          order: "desc",
        };
      } else {
        this.sort = sort;
      }
    },

    /**
     * 위험성평가 상세 Raw data 엑셀 다운로드
     *
     * 화면의 검색 조건과 탭을 그대로 사용해 목록 전체를 받아, 위험성평가표 행 단위로 펼쳐 저장한다.
     * 전체 탭은 직영/수급업체가 한 파일에 섞이지만, 엑셀의 "구분" 컬럼으로 나눠 볼 수 있다.
     */
    async onClickRiskRawExcel() {
      this.loading(true);
      try {
        const target = this.riskRawExcelTarget();
        const items = await this.loadAllPostsForRawExcel(target.boardId, target.withBoardId);

        // 팀(업체)명/공장명/대·중·소분류를 736/895 최신값으로 맞춘다 (내부에서 100건씩 나눠 조회)
        const withLatest = await this.attachLatestClassification(items);

        const count = await downloadRiskRawExcel(withLatest, target.label, target.boardId);
        console.log(`[RawExcel] 문서 ${withLatest.length}건 -> ${count}행`);
      } catch (e) {
        console.error("onClickRiskRawExcel err", e);
      } finally {
        this.loading(false);
      }
    },

    /** 현재 탭에 해당하는 조회 대상과 파일명 */
    riskRawExcelTarget() {
      if (this.tabIndex === 1) return { boardId: 813, withBoardId: 0, label: "위험성평가_직영" };
      if (this.tabIndex === 2) return { boardId: 814, withBoardId: 0, label: "위험성평가_수급업체" };
      return { boardId: 813, withBoardId: 814, label: "위험성평가_전체" };
    },

    /**
     * 현재 검색 조건으로 승인 완료 문서 전체를 페이지 단위로 모두 받아온다.
     *
     * List Excel 다운로드는 perPage 를 total 로 잡아 한 번에 받지만, Raw data 는 문서 본문까지
     * 전부 필요해 응답이 훨씬 커진다. 여기서는 100건씩 나눠 받는다.
     */
    async loadAllPostsForRawExcel(boardId, withBoardId) {
      const conditions = await this.getConditions();

      const options = {
        projectId: this.projectId,
        boardSlug: `board-${boardId}`,
        isParent: false,
        perPage: 100,
        conditions,
        orderTarget: "updated_at",
        orderDirection: "desc",
        statusIds: [4] /*4:승인*/,
      };
      if (withBoardId) {
        options.withSlugs = [`board-${withBoardId}`];
      }

      const items = [];
      let page = 1;
      let lastPage = 1;

      do {
        const response = await this.$store.dispatch("approval/LOAD_POST_ALL", { ...options, page });
        items.push(...((response && response.data) || []));
        lastPage = Number(response && response.last_page) || 1;
        page += 1;
      } while (page <= lastPage);

      return items;
    },

    async onClickExcelDownload() {
      this.loading(true);

      const originalPage = this.page;
      const originalPerPage = this.perPage;
      const originalLastPage = this.lastPage;
      const originalTotal = this.total;

      this.page = 1;
      const perPage = this.total + 10;

      // 전체 리스트를 불러온다
      if (this.tabIndex === 0) {
        this.excelList = await this.loadBoardList(perPage, undefined, 813, 814);
      } else if (this.tabIndex === 1) {
        this.excelList = await this.loadBoardList(perPage, undefined, 813);
      } else if (this.tabIndex === 2) {
        this.excelList = await this.loadBoardList(perPage, undefined, 814);
      }

      this.page = originalPage;
      this.perPage = originalPerPage;
      this.lastPage = originalLastPage;
      this.total = originalTotal;

      this.loading(false);
      this.openExcelDownload = true;
    },

    setListSort(list) {
      list.sort((a, b) => {
        const contentA = JSON.parse(a.post_content);
        const contentB = JSON.parse(b.post_content);
        const sort = this.sort;

        let sortTarget = 0;
        if (sort.key === "no") {
          const valueA = this.findJsonDataWithDottedPath(contentA, this.sortDefaultKey);
          const valueB = this.findJsonDataWithDottedPath(contentB, this.sortDefaultKey);
          if (valueA === valueB) {
            sortTarget = 0;
          } else {
            sortTarget = valueA > valueB ? 1 : -1;
          }
        } else if (sort.key === "boardId") {
          const A구분 = a.board_id === 813 ? "직영" : "수급업체";
          const B구분 = a.board_id === 813 ? "직영" : "수급업체";
          if (A구분 === B구분) {
            sortTarget = 0;
          } else {
            sortTarget = A구분 > B구분 ? 1 : -1;
          }
        } else if (sort.key === "approval") {
          sortTarget = a.document_status.id - b.document_status.id;
        } else if (sort.key === "created_at") {
          sortTarget = new Date(a.created_at) - new Date(b.created_at);
        } else {
          const valueA = this.findJsonDataWithDottedPath(contentA, sort.key);
          const valueB = this.findJsonDataWithDottedPath(contentB, sort.key);
          if (valueA === valueB) {
            sortTarget = 0;
          } else {
            sortTarget = valueA > valueB ? 1 : -1;
          }
        }

        return sortTarget * (sort.order === "asc" ? 1 : -1);
      });
    },

    reloadList() {
      // 페이지를 1로 초기화하고 리스트 다시 불러오기
      this.page = 1;
      this.$refs.listPageLayout.pagination.default = 1;
      this.$refs.listPageLayout.loadList({
        page: 1,
        perPage: this.perPage,
        func: this.defaultFunc,
      });
      this.$refs.listPageLayout.setPage(1);
    },
    // ----------------

    getContent(data) {
      if (data.post_content) {
        const content = JSON.parse(data.post_content);
        return content.body;
      }
    },
    getCreatedDate(data) {
      if (data === "") return "";
      return moment(data).format("YYYY-MM-DD");
    },
    getCompanyName(comId) {
      for (const com of this.listCompanyList) {
        if (com.id === comId) {
          return com.com_name;
        }
      }
    },
    getContentTargetList(list) {
      let result = "";
      for (const data of list) {
        if (!result.includes(data.target)) {
          result += `${data.target},`;
        }
      }
      return result.substring(0, result.length - 1);
    },
    onNameBlur(name) {
      const length = name.length;
      if (length > 2) {
        const first = name[0];
        const last = name[name.length - 1];
        let star = "";
        for (let index = 0; index < length - 2; index++) {
          star += "*";
        }
        name = first + star + last;
      } else if (length === 2) {
        const first = name[0];
        name = `${first}*`;
      }
      return name;
    },
    getContentRiskList(list) {
      let result = "";
      for (const data of list) {
        if (data.input_3) {
          if (typeof data.input_3 === "string") {
            if (!result.includes(data.input_3)) {
              result += `${data.input_3},`;
            }
          }

          if (typeof data.input_3 === "object") {
            const temp = data.input_3.toString().split(",");
            for (const t of temp) {
              if (t && !result.includes(t)) {
                result += `${t},`;
              }
            }
          }
        }
      }
      return result.substring(0, result.length - 1);
    },
    getContentStringList(list) {
      let result = "";
      for (const data of list) {
        if (!result.includes(data)) {
          result += `${data},`;
        }
      }
      return result.substring(0, result.length - 1);
    },
    getAuthUserInfo() {
      return this.$store.state.auth.userInfo;
    },
    comma(str) {
      return `${str}`.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },
};
</script>

<style scoped></style>
